import { IonCard, IonCol, IonGrid, IonIcon, IonItem, IonLabel, IonRow, IonText } from "@ionic/react";
import { CollapsableTable }    from "components/CollapsableTable";
import { OverviewCard }        from "components/OverviewCard";
import { Page }                from "components/Page";
import { DvcPage }             from "navigation/DvcPage";
import { RouterMap }           from "navigation/RouterMap";
import { FormatNumber, ModCosFormatGCR } from "functions/Formatting";
import { Glob, Loc, useLoc }          from "functions/Language";
import { GCR12_Stages, IsFromDevice, TYPE_GCR, TYPE_GCRT, TYPE_ICR } from "functions/networking/NetTypes";
import { useLoader }                            from "functions/networking/useLoader";
import { FC } from "react";

import capIcon from "img/capacity.svg";
import indIcon from "img/induction.svg";
import { lockClosedOutline } from "ionicons/icons";
import { phaseColors } from "gconstants";


const route = RouterMap.stages;
const Link : FC = () => {
	const loc = useLoc(glob);
	return <IonItem routerLink={route}>
		<IonLabel>{loc.title}</IonLabel>
	</IonItem>
}

function seconds_to_hms(time: number) {
	if (isNaN(time)) return "--:--:--";
	else return `${Math.floor(time / 3600).toFixed(0)}:${Math.floor(time / 60 % 60).toFixed(0).padStart(2, '0')}:${(time % 60).toFixed(0).padStart(2, '0')}`;
}

function format_power(power: number) {
	let icon;
	if      (power  <  0) icon = capIcon;
	else if (power === 0) icon = null;
	else if (power  >  0) icon = indIcon;

	const str = Math.abs(power).toFixed(1);
	const dot = str.indexOf('.');

	return <>
		<span className="left">
			{icon && <IonIcon icon={icon} style={{marginInline: "2px"}} />}
			{str.slice(0, dot+1)}
		</span>
		<span className="right">
			{str.slice(dot+1)}&nbsp;kVAr
		</span>
	</>
}

const StageDisplay : FC<{
	data: GCR12_Stages[number],
	index: number,
	loc: Loc<typeof glob>,
	expanded: boolean,
	phase: boolean,
}> = (props) => {
	return <IonRow style={props.index%3 === 2 ? { borderBottom: "solid 1px var(--ion-color-medium)" } : {}}>
		<IonCol size={props.phase ? "2.5" : "3"}>{props.loc.stage_nr(props.index+1)}</IonCol>
		{props.phase && <IonCol size=".5">
			<span style={{color: phaseColors[props.data.l-1], display:"inline list-item", fontSize:"3ex", lineHeight: 0, listStyle: "square"}}></span>
			L{props.data.l}
		</IonCol>}

		<IonCol size={props.expanded ? "1" : undefined}>
			<span style={{width: "50%", display: "inline-block", textAlign: "end", paddingInlineEnd: "1ex"}}>
				<IonIcon icon={lockClosedOutline} style={{visibility: props.data.fixed ? "visible" : "hidden"}} />
			</span>
			<IonText color={props.data.state ? "success" : "danger"} style={{width: "50%", display: "inline-block", textAlign: "left"}}>
				{props.data.state ? "ON" : "OFF"}
			</IonText>
		</IonCol>

		<IonCol>
			<IonText color={props.data.state ? "dark" : "medium"}>
				{format_power(props.data.pow/10)}
			</IonText>
		</IonCol>

		{props.expanded && <>
			<IonCol>
				{seconds_to_hms(props.data.time)}
			</IonCol>

			{props.data.fast
				? <IonCol>{props.loc.fast}</IonCol>
				: <IonCol>
					<span className="left">
						{props.data.nr_op} /
					</span>
					<span className="right">
						&nbsp;{props.data.max_nr_op}
					</span>
				</IonCol>
			}
		</>}
	</IonRow>
}

const Content : FC = () => {
	const loc = useLoc(glob);
	const loader = useLoader("Stages", null, { watchOthers: true });
	const pLoader = useLoader("Online", null);

	const loaders = [loader, pLoader];

	const onlineData = (pLoader.data && (IsFromDevice(pLoader.data, [TYPE_GCR, TYPE_GCRT, TYPE_ICR]))) ? pLoader.data.Data : null;

	const phase = loader.data !== null && loader.data.Data.some(st => st.l !== undefined);

	return <Page title={loc.title} loaders={loaders}>
		<IonCard style={{overflowX: "auto", maxWidth: "calc(max(50%, 100em))"}}>
			<IonGrid class="table" style={{minWidth: "54em"}}>
				<IonRow class="header bheader">
					<IonCol size="3" />
					<IonCol size="1">{loc.state}</IonCol>
					<IonCol>{loc.power}</IonCol>
					<IonCol>{loc.time}</IonCol>
					<IonCol>{loc.nr_op}</IonCol>
				</IonRow>

				{loader.data?.Data.map((v, i) => <StageDisplay key={i} data={v} index={i} loc={loc} expanded={true} phase={phase} />)}
			</IonGrid>
		</IonCard>

		{onlineData && <IonCard style={{ maxWidth: "50em" }}>
			<CollapsableTable
				title={loc.quantity}
				data={[
					[
						{ label: loc.CosFi,         values: onlineData.CosFi_Mod, formatter: ModCosFormatGCR },
						{ label: loc.CosFi_Avg,     values: onlineData.CosFi_Avg, formatter: FormatNumber },
					],
					[
						{ label: "S",               values: onlineData.S,         formatter: FormatNumber, unit: "kVA",  mult: 1e-3 },
						{ label: "P",               values: onlineData.P,         formatter: FormatNumber, unit: "kW",   mult: 1e-3 },
						{ label: "Q",               values: onlineData.Q,         formatter: FormatNumber, unit: "kVAr", mult: 1e-3 },
					],
					[
						{ label: loc.allowed_q,     values: onlineData.Allowed_Q, formatter: FormatNumber, unit: "kVAr", mult: 1e-3 },
						{ label: loc.required_q,    values: onlineData.Reqd_Q,    formatter: FormatNumber, unit: "kVAr", mult: 1e-3 },
					],
					[
						{ label: "SHTD+",           values: onlineData["SHTD_p"], formatter: n => n.toFixed(0) },
						{ label: "SHTD-",           values: onlineData["SHTD_m" as keyof typeof onlineData], formatter: n => n.toFixed(0) },
					],
				]}
			/>
		</IonCard>}
	</Page>
}

const Overview: FC = () => {
	const loc = useLoc(glob);
	const loader = useLoader("Stages", null, { watchOthers: true });
	const phase = loader.data !== null && loader.data.Data.some(st => st.l !== undefined);

	return <OverviewCard href={route} title={loc.title}>
		<IonGrid class="table" style={{ margin: "5px" }}>
			<IonRow class="header bheader">
				<IonCol size="3" />
				<IonCol>{loc.state}</IonCol>
				<IonCol>{loc.power}</IonCol>
			</IonRow>

			{loader.data?.Data.map((v, i) => <StageDisplay key={i} data={v} index={i} loc={loc} expanded={false} phase={phase} />)}
		</IonGrid>
	</OverviewCard>
}

const glob = Glob({
	en: {
		title: "Stages",

		stage_nr: (n: number) => `Stage ${n}`,
		fast: "Fast stage",

		state: "State",
		power: "Power",
		time:  "Total ON time",
		nr_op: "Number of operations",

		quantity: "Quantity",
		value: "Value",
		allowed_q:  "Allowed Q",
		required_q: "Missing Q",
		CosFi: "cos φ",
		CosFi_Avg: "Average cos φ",
	},
	cs: {
		title: "Stupně",

		stage_nr: (n: number) => `Stupeň ${n}`,
		fast: "Rychlý stupeň",

		state: "Stav",
		power: "Výkon",
		time:  "Celková doba ON",
		nr_op: "Počet sepnutí",

		quantity: "Veličina",
		value: "Hodnota",
		allowed_q:  "Povolený Q",
		required_q: "Chybějící Q",
		CosFi: "cos φ",
		CosFi_Avg: "Průměrný cos φ",
	},
	pg: {
		title: "Estágios",

		stage_nr: (n: number) => `Estágio ${n}`,
		fast: "Estágio rápido",

		state: "Status",
		power: "Potência",
		time: "Tempo total ON",
		nr_op: "Número de ativações",

		quantity: "Quantidade",
		value: "Valor",
		allowed_q:  "Q permitida",
		required_q: "Q ausente",
		CosFi: "cos φ",
		CosFi_Avg: "cos φ médio"
	},
	es: {
		title: "Etapas",

		stage_nr: (n: number) => `Etapa ${n}`,
		fast: "Etapa rápido",

		state: "Status",
		power: "Potencia",
		time: "Tiempo total ON",
		nr_op: "Número de activaciones",

		quantity: "Cantidad",
		value: "Valor",
		allowed_q:  "Q permitida",
		required_q: "Q faltante",
		CosFi: "cos φ",
		CosFi_Avg: "cos φ medio"
	},
} as const);


export const Stages = {
	Link: Link,
	Overview: Overview,
}

export const PStages : DvcPage = {
	page: Content,
	path: route,
	title: glob,
	icon: capIcon,

	available(dvc) {
		return dvc._type === TYPE_GCR || dvc._type === TYPE_GCRT || dvc._type === TYPE_ICR;
	}
}
